import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';

// ============ Types ============

export interface AnypayBalance {
  available: number;
  pending: number;
  total: number;
  currency: string;
  lastUpdated: string;
  taxes: {
    cashInPercentage: string;
    cashOutFixed: string;
  };
}

export interface AnypayPayment {
  id: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  amount: number;
  pixCode?: string;
  qrCodeImage?: string;
  paidAt?: string;
  createdAt: string;
}

export interface AnypayWithdrawal {
  id: string;
  status: 'pending' | 'completed' | 'failed';
  amount: number;
  player_id: string;
  player_pix_key: string;
  createdAt: string;
}

export interface AnypayConnectionTest {
  connected: boolean;
  provider: string;
}

// ============ Hooks ============

export function useAnypayBalance() {
  return useQuery<AnypayBalance>({
    queryKey: ['anypay', 'balance'],
    queryFn: async () => {
      const { data } = await api.get(ENDPOINTS.anypay.balance);
      return data;
    },
    refetchInterval: 30000, // Refresh every 30s
  });
}

export function useAnypayPayments(filters?: {
  status?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery<{ success: boolean; data: AnypayPayment[]; pagination: any }>({
    queryKey: ['anypay', 'payments', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.offset) params.append('offset', String(filters.offset));
      const query = params.toString();
      const { data } = await api.get(`${ENDPOINTS.anypay.payments}${query ? `?${query}` : ''}`);
      return data;
    },
  });
}

export function useAnypayWithdrawals(filters?: {
  status?: string;
  limit?: number;
}) {
  return useQuery<{ success: boolean; data: AnypayWithdrawal[]; pagination: any }>({
    queryKey: ['anypay', 'withdrawals', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.limit) params.append('limit', String(filters.limit));
      const query = params.toString();
      const { data } = await api.get(`${ENDPOINTS.anypay.withdrawals}${query ? `?${query}` : ''}`);
      return data;
    },
    retry: (failureCount, error: any) => {
      // Don't retry on 401/403 - these are auth/permission issues
      if (error?.response?.status === 401 || error?.response?.status === 403) return false;
      return failureCount < 2;
    },
  });
}

export function useAnypayTestConnection() {
  return useQuery<AnypayConnectionTest>({
    queryKey: ['anypay', 'connection'],
    queryFn: async () => {
      const { data } = await api.get(ENDPOINTS.anypay.testConnection);
      return data;
    },
    enabled: false, // Only run when manually triggered
  });
}

export interface AnypayTransaction {
  id: string;
  type: string;
  status: string;
  grossAmount: number;
  totalFees: number;
  netAmount: number;
  correlationId: string;
  description: string;
  depositType: string;
  processorId: string;
  createdAt: string;
  processedAt?: string;
}

export interface AnypayTransactionSummary {
  totalReceived: number;
  totalReceivedConfirmed: number;
  totalPending: number;
  totalFees: number;
  totalNet: number;
  cashInPercentage: number;
  escrowBalance: number;
  totalWithdrawn: number;
  confirmedCount: number;
  pendingCount: number;
  failedCount: number;
  totalCount: number;
  balanceBased: boolean;
}

export interface AnypayTransactionsResponse {
  transactions: AnypayTransaction[];
  total: number;
  summary: AnypayTransactionSummary;
}

export function useAnypayTransactions(filters?: {
  status?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery<AnypayTransactionsResponse>({
    queryKey: ['anypay', 'transactions', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.offset) params.append('offset', String(filters.offset));
      const query = params.toString();
      const { data } = await api.get(`${ENDPOINTS.anypay.transactions}${query ? `?${query}` : ''}`);
      return data;
    },
    refetchInterval: 60000, // Refresh every 60s
  });
}

export function useAnypayWithdraw() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: {
      pixKey: string;
      pixKeyType?: string;
      amount: number;
      description?: string;
      recipientName: string;
      recipientDocument: string;
    }) => {
      const { data } = await api.post(ENDPOINTS.anypay.withdraw, dto);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['anypay', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['anypay', 'withdrawals'] });
    },
  });
}

// ============ Sync ============

export interface AnpaySyncResult {
  totalFromApi: number;
  synced: number;
  skipped: number;
  failed: number;
  errors: string[];
  syncedTransactions: Array<{
    anypayId: string;
    type: string;
    grossAmount: number;
    status: string;
  }>;
}

export function useAnypaySync() {
  const queryClient = useQueryClient();

  return useMutation<AnpaySyncResult>({
    mutationFn: async () => {
      const { data } = await api.post(ENDPOINTS.anypay.sync);
      return data;
    },
    onSuccess: () => {
      // Invalidate all related queries so reports refresh
      queryClient.invalidateQueries({ queryKey: ['anypay'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['financial-reports'] });
    },
  });
}
